/**
 * Updated Course-related type definitions for trAInedup.ai
 * Simplified to match the specific requirements for the learning management system
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Course difficulty levels
 */
export enum CourseDifficulty {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
}

/**
 * Module types as specified in requirements
 */
export enum ModuleType {
  VIDEO = 'video',      // Video with transcription
  AUDIO = 'audio',      // Audio with transcription  
  PDF = 'pdf',          // PDF viewer
  TEXT = 'text',        // Long form text in chunks
  QUIZ = 'quiz',        // Interactive quiz with scoring
}

/**
 * Progress status
 */
export enum ProgressStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

/**
 * Course interface - simplified for requirements
 */
export interface Course {
  id: string;
  title: string;
  description: string;           // Detailed description
  shortDescription: string;      // Brief description for catalog
  category: string;              // Course category
  difficulty: CourseDifficulty;
  estimatedDuration: number;     // Total duration in minutes
  
  // Visual assets
  thumbnail: string;             // Firebase Storage URL
  coverImage: string;            // Firebase Storage URL
  
  // Course structure
  moduleIds: string[];           // Array of module IDs in order
  totalModules: number;          // Total number of modules
  
  // Access control
  isPublished: boolean;          // Whether course is live
  isFree: boolean;              // Whether course is free (Ethics and AI - Introduction)
  requiredSubscriptionTier: string; // 'free' | 'basic' | 'premium'
  
  // Metadata
  tags: string[];               // Searchable tags
  createdBy: string;            // User ID of creator
  createdAt: Timestamp;
  updatedAt: Timestamp;
  
  // Engagement metrics
  enrollmentCount: number;      // Total enrollments
  averageRating: number;        // Average user rating
  reviewCount: number;          // Number of reviews
  
  // AI-extracted metadata (optional, cached)
  extractedTopics?: string[];   // Skills/topics extracted from course content
  topicsExtractedAt?: Timestamp; // When topics were last extracted
}

/**
 * Module interface - supports the 5 required types
 */
export interface Module {
  id: string;
  courseId: string;             // Reference to parent course
  title: string;                // Module title
  description: string;          // Module description
  order: number;                // Order within course (0-based)
  type: ModuleType;             // One of the 5 supported types
  
  // Content based on type
  content: ModuleContent;
  
  // Progress requirements
  isRequired: boolean;          // Must be completed to progress
  estimatedDuration: number;    // Duration in minutes
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Module content - varies by type
 */
export interface ModuleContent {
  // Video module
  videoUrl?: string;            // Firebase Storage URL
  videoTranscript?: string;     // Full transcript text
  videoDuration?: number;       // Duration in seconds
  
  // Audio module  
  audioUrl?: string;            // Firebase Storage URL
  audioTranscript?: string;     // Full transcript text
  audioDuration?: number;       // Duration in seconds
  
  // PDF module
  pdfUrl?: string;              // Firebase Storage URL
  pdfTitle?: string;            // PDF document title
  pdfPageCount?: number;        // Number of pages
  
  // Text module
  text?: string;                // Text content for all module types
  textContent?: string;         // Formatted text content (HTML/Markdown) - legacy
  textChunks?: TextChunk[];     // Chunked content for better readability
  
  // Quiz module
  quiz?: Quiz;                  // Quiz data structure
}

/**
 * Text chunk for readable formatting
 */
export interface TextChunk {
  id: string;
  title: string;                // Chunk title/heading
  content: string;              // Chunk content
  order: number;                // Order within module
}

/**
 * Quiz interface with scoring and pass marks
 */
export interface Quiz {
  title: string;
  description?: string;
  questions: QuizQuestion[];
  passingScore: number;         // Percentage required to pass (0-100)
  maxAttempts?: number;         // Maximum number of attempts allowed
}

export enum QuizQuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  TRUE_FALSE = 'true_false',
  SHORT_ANSWER = 'short_answer',
  MULTIPLE_RESPONSE = 'multiple_response', // New
  MATCHING = 'matching',             // New
}

export interface BaseQuizQuestion {
  id: string;
  question: string;             // Question text
  explanation?: string;         // Explanation of correct answer
  points: number;               // Points for this question
  order: number;                // Order within quiz
}

export interface MultipleChoiceQuestion extends BaseQuizQuestion {
  type: QuizQuestionType.MULTIPLE_CHOICE;
  options: string[];
  correctAnswer: number;        // Index of the correct option
}

export interface TrueFalseQuestion extends BaseQuizQuestion {
  type: QuizQuestionType.TRUE_FALSE;
  correctAnswer: boolean;       // True or False
}

export interface ShortAnswerQuestion extends BaseQuizQuestion {
  type: QuizQuestionType.SHORT_ANSWER;
  correctAnswer: string | string[]; // Single string or array of acceptable strings
  blankPlaceholder?: string;    // Optional placeholder for input field
}

export interface MultipleResponseQuestion extends BaseQuizQuestion {
  type: QuizQuestionType.MULTIPLE_RESPONSE;
  options: string[];
  correctAnswer: number[];      // Array of indices of correct options
}

export interface MatchingQuestion extends BaseQuizQuestion {
  type: QuizQuestionType.MATCHING;
  leftItems: string[];
  rightItems: string[];
  correctAnswer: number[];      // Array where correctAnswer[i] is the index of rightItems matching leftItems[i]
}

export type QuizQuestion =
  | MultipleChoiceQuestion
  | TrueFalseQuestion
  | ShortAnswerQuestion
  | MultipleResponseQuestion
  | MatchingQuestion;

/**
 * User progress tracking
 */
export interface UserProgress {
  id: string;
  userId: string;               // Reference to user
  courseId: string;             // Reference to course
  
  // Overall progress
  status: ProgressStatus;
  enrolledAt: Timestamp;        // When user enrolled
  startedAt?: Timestamp;        // When user started first module
  completedAt?: Timestamp;      // When user completed all modules
  lastAccessedAt: Timestamp;    // Last time user accessed course
  
  // Module progress
  moduleProgress: ModuleProgress[];
  completedModules: number;     // Count of completed modules
  totalModules: number;         // Total modules in course
  completionPercentage: number; // Overall completion percentage
  
  // Time tracking
  totalTimeSpent: number;       // Total time in minutes
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Module progress tracking
 */
export interface ModuleProgress {
  moduleId: string;
  status: ProgressStatus;
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  timeSpent: number;            // Time spent in minutes
  
  // Video/Audio specific
  watchProgress?: number;       // Percentage watched/listened (0-100)
  
  // Quiz specific
  quizAttempts?: QuizAttempt[];
  bestScore?: number;           // Best quiz score achieved
  passed?: boolean;             // Whether user passed the quiz
}

/**
 * Quiz attempt tracking
 */
export interface QuizAttempt {
  userId: string;
  courseId: string;
  moduleId: string;
  attemptNumber: number;
  startedAt: Timestamp;
  completedAt?: Timestamp;
  score: number;                // Score as percentage (0-100)
  passed: boolean;              // Whether attempt passed
  answers: QuizAnswer[];        // User's answers
  timeSpent: number;            // Time spent in minutes
}

/**
 * Quiz answer tracking
 */
export interface QuizAnswer {
  questionId: string;
  userAnswer: string | number;  // User's answer
  isCorrect: boolean;           // Whether answer was correct
  pointsEarned: number;         // Points earned for this answer
}

/**
 * User favorites
 */
export interface UserFavorite {
  id: string;
  userId: string;               // Reference to user
  courseId: string;             // Reference to favorited course
  createdAt: Timestamp;         // When course was favorited
}

/**
 * Course reviews
 */
export interface CourseReview {
  id: string;
  courseId: string;             // Reference to course
  userId: string;               // Reference to reviewer
  rating: number;               // Rating 1-5
  review?: string;              // Optional review text
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Detailed course feedback (submitted via course review module)
 */
export interface CourseReviewFeedback {
  id: string;
  courseId: string;             // Reference to course
  userId: string;               // Reference to user
  
  // Required ratings (1-5 scale)
  overallRating: number;        // Overall course rating
  contentQualityRating: number; // How clear and comprehensive
  practicalValueRating: number; // Real-world applicability
  
  // Required categorical feedback
  difficultyLevel: 'too_easy' | 'just_right' | 'too_challenging';
  pace: 'too_slow' | 'just_right' | 'too_fast';
  wouldRecommend: boolean;      // Would you recommend this course?
  
  // Optional feedback
  whatWorkedWell?: string;      // Open text feedback
  whatCouldImprove?: string;    // Open text feedback
  skillsGained?: string[];      // Multi-select based on course objectives
  nextLearningGoal?: string;    // What they want to learn next
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Course catalog filter options
 */
export interface CourseFilters {
  searchTerm?: string;
  category?: string;
  difficulty?: CourseDifficulty;
  isFree?: boolean;
  showFavoritesOnly?: boolean;
  showCompletedOnly?: boolean;
  status?: 'published' | 'draft';
}

/**
 * Course creation form data
 */
export interface CourseCreationData {
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  difficulty: CourseDifficulty;
  tags: string[];
  isFree: boolean;
  requiredSubscriptionTier: string;
  thumbnail?: File;
  coverImage?: File;
}

/**
 * Module creation form data
 */
export interface ModuleCreationData {
  title: string;
  description: string;
  type: ModuleType;
  isRequired: boolean;
  estimatedDuration: number;
  
  // Content files
  videoFile?: File;
  audioFile?: File;
  pdfFile?: File;
  
  // Text content
  textContent?: string;
  textChunks?: Omit<TextChunk, 'id'>[];
  
  // Quiz data
  quiz?: Omit<Quiz, 'questions'> & {
    questions: Omit<QuizQuestion, 'id'>[];
  };
  
  // Transcripts
  videoTranscript?: string;
  audioTranscript?: string;
}

// Legacy enums for backward compatibility
export enum CourseCategory {
  AI_RESEARCH_LEARNING_TOOLS = 'AI Research & Learning Tools',
  AI_MARKETING_COPYWRITING = 'AI Marketing & Copywriting',
  AI_PRODUCTIVITY_WORKFLOW_MANAGEMENT = 'AI Productivity & Workflow Management',
  AI_CONTENT_BLOGGING = 'AI Content & Blogging',
  AI_CREATIVE_ARTS_MEDIA = 'AI Creative Arts & Media',
  AI_DATA_ANALYTICS = 'AI Data & Analytics',
  AI_COMMUNICATION_MEETING_TOOLS = 'AI Communication & Meeting Tools',
  AI_BUSINESS_ENTREPRENEURSHIP = 'AI Business & Entrepreneurship',
  AI_PRODUCTIVITY_TIME_MANAGEMENT = 'AI Productivity & Time Management',
  AI_PROJECT_COLLABORATION_TOOLS = 'AI Project & Collaboration Tools',
  AI_LANGUAGE_TRANSLATION = 'AI Language & Translation',
  AI_DEVELOPMENT_CODING = 'AI Development & Coding',
  AI_FINANCE_ACCOUNTING = 'AI Finance & Accounting',
  AI_HR_RECRUITING = 'AI HR & Recruiting',
  AI_TOOLS = 'AI Tools',
}

// Centralized category list and default
export const COURSE_CATEGORIES = Object.values(CourseCategory) as readonly CourseCategory[];
export const DEFAULT_COURSE_CATEGORY: CourseCategory = COURSE_CATEGORIES[0];

export enum LessonType {
  VIDEO = 'video',
  TEXT = 'text',
  INTERACTIVE = 'interactive',
  QUIZ = 'quiz',
  ASSIGNMENT = 'assignment',
  LIVE_SESSION = 'live_session',
  SLIDE = 'slide',
}

export enum AssessmentType {
  MULTIPLE_CHOICE = 'multiple_choice',
  TRUE_FALSE = 'true_false',
  SHORT_ANSWER = 'short_answer',
  ESSAY = 'essay',
  PRACTICAL = 'practical',
  PROJECT = 'project',
}

const courseTypes = {
  CourseDifficulty,
  CourseCategory,
  COURSE_CATEGORIES,
  DEFAULT_COURSE_CATEGORY,
  ModuleType,
  LessonType,
  AssessmentType,
  ProgressStatus,
  QuizQuestionType,
};

export default courseTypes;

